import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useRef,
    useState
} from 'react';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import {AuthData, Authorization, AuthTokens} from '../types';
import {decodeJwt} from '../lib/jwt';
import isLocalOrDevEnvironment from '../lib/isLocalOrDevEnvironment';
import isAuthError from '../lib/isAuthError';
import isProblemDetails from '../lib/isProblemDetails';
import unknownErrorToString from '../lib/unknownErrorToString';
import refreshAuthentication from '../api/auth/refreshAuthentication';
import {RoutePaths} from '../RoutePaths';

const tenSecondsInMilliseconds = 10 * 1000;

type StorageKey = 'access_token' | 'refresh_token' | 'redir';

function tokenHasExpired(access_token: string): boolean {
    const {payload} = decodeJwt(access_token);
    const expires = Number(payload.exp);
    const expiryDate = new Date(expires * 1000);
    return Date.now() >= expiryDate.getTime() - tenSecondsInMilliseconds;
}

function getAuthData(access_token: string | null | undefined): AuthData {
    if (!access_token) {
        return {isAuthenticated: false};
    }

    try {
        const {payload} = decodeJwt(access_token);
        return {
            isAuthenticated: true,
            id: payload.nameid,
            unique_name: payload.unique_name,
            email: payload.email,
            given_name: payload.given_name
        };
    } catch {
        return {isAuthenticated: false};
    }
}

// Tokens live in localStorage on localhost (survives dev-server reloads) and sessionStorage
// elsewhere.
function storage() {
    return isLocalOrDevEnvironment ? window.localStorage : window.sessionStorage;
}

function getItem(key: StorageKey) {
    try {
        return storage().getItem(key) ?? null;
    } catch {
        return null;
    }
}

function setItem(key: StorageKey, value: string | null | undefined) {
    try {
        storage().setItem(key, value ?? '');
    } catch {
        // Storage unavailable (private mode); the session simply will not persist.
    }
}

function removeItem(key: StorageKey) {
    try {
        storage().removeItem(key);
    } catch {
        // ignore
    }
}

export type AuthStateManager = {
    access_token: string;
    authData: AuthData;
    isAuthenticated: boolean;
    email: string | null;
    isAuthorizedForResource(resource: Authorization): boolean;
    logout(): void;
    setSignInRedirect(redirect: string): void;
    removeSignInRedirect(): string | null;
    redirectOnAuthenticated(redir?: string | null): void;
    getAccessToken(): Promise<string>;
    onAuthenticated(authResponse: AuthTokens): string;
};

const emptyAuthStateManager: AuthStateManager = {
    access_token: '',
    authData: {isAuthenticated: false},
    isAuthenticated: false,
    email: null,
    isAuthorizedForResource: () => false,
    logout: () => {},
    setSignInRedirect: () => {},
    removeSignInRedirect: () => null,
    redirectOnAuthenticated: () => {},
    getAccessToken: async () => '',
    onAuthenticated: () => ''
};

const AuthStateManagerContext = createContext<AuthStateManager>(
    emptyAuthStateManager
);

function useAuthStateManagerImpl(): AuthStateManager {
    const navigate = useNavigate();

    const [access_token, set_access_token] = useState(
        () => getItem('access_token') ?? ''
    );
    const [refresh_token, set_refresh_token] = useState(
        () => getItem('refresh_token') ?? ''
    );
    const [authData, setAuthData] = useState(() => getAuthData(access_token));

    const refreshingPromiseRef = useRef<Promise<string> | null>(null);

    const isAuthenticated = authData.isAuthenticated;

    const isAuthorizedForResource = (resource: Authorization): boolean =>
        !resource.authorize || isAuthenticated;

    const logout = useCallback(() => {
        set_access_token('');
        set_refresh_token('');
        removeItem('access_token');
        removeItem('refresh_token');
        setAuthData({isAuthenticated: false});
    }, []);

    const setSignInRedirect = (redirect: string) => setItem('redir', redirect);

    const removeSignInRedirect = () => {
        const redirect = getItem('redir');
        removeItem('redir');
        return redirect;
    };

    const redirectOnAuthenticated = (redir?: string | null) => {
        if (redir) {
            navigate(redir);
            return;
        }

        const storedRedirect = removeSignInRedirect();
        navigate(storedRedirect || RoutePaths.home());
    };

    const onAuthenticated = useCallback((authResponse: AuthTokens) => {
        set_access_token(authResponse.access_token);
        set_refresh_token(authResponse.refresh_token);
        setItem('access_token', authResponse.access_token);
        setItem('refresh_token', authResponse.refresh_token);
        setAuthData(getAuthData(authResponse.access_token));
        return authResponse.access_token;
    }, []);

    const refreshTokenRequest = useCallback(async () => {
        const authResponse = await refreshAuthentication(refresh_token ?? '');

        if (isAuthError(authResponse) || isProblemDetails(authResponse)) {
            const errorMessage = isProblemDetails(authResponse)
                ? unknownErrorToString(authResponse)
                : 'Your login session has expired, please login again.';
            toast.error(errorMessage);
            logout();
            return '';
        }

        if (!authResponse.access_token) {
            toast.error('Session refresh failed. Please log in again.');
            logout();
            return '';
        }

        return onAuthenticated(authResponse);
    }, [refresh_token, onAuthenticated, logout]);

    const getAccessToken = useCallback(async () => {
        if (!access_token) {
            return '';
        }

        if (!tokenHasExpired(access_token)) {
            return access_token;
        }

        if (refreshingPromiseRef.current) {
            try {
                return await refreshingPromiseRef.current;
            } catch {
                logout();
                return '';
            }
        }

        const refreshPromise = refreshTokenRequest();
        refreshingPromiseRef.current = refreshPromise;
        try {
            return await refreshPromise;
        } catch {
            return '';
        } finally {
            refreshingPromiseRef.current = null;
        }
    }, [access_token, refreshTokenRequest, logout]);

    return {
        access_token,
        authData,
        isAuthenticated,
        email: isAuthenticated ? authData.email : null,
        isAuthorizedForResource,
        logout,
        setSignInRedirect,
        removeSignInRedirect,
        redirectOnAuthenticated,
        getAccessToken,
        onAuthenticated
    };
}

const AuthStateManagerProvider = ({children}: {children: ReactNode}) => {
    const authStateManager = useAuthStateManagerImpl();
    return (
        <AuthStateManagerContext.Provider value={authStateManager}>
            {children}
        </AuthStateManagerContext.Provider>
    );
};

const useAuthStateManager = () => useContext(AuthStateManagerContext);

// eslint-disable-next-line react-refresh/only-export-components
export {useAuthStateManager, AuthStateManagerProvider};
